import React, { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import API_ENDPOINTS from "../../api/apiConfig";
import { env } from "../../config/env";
import "../../assets/css/theme.css";
import "../../assets/css/shop.css";   // shared page-header band (.ffs-hero*)
import "../../assets/css/pages.css";

const DeleteAccountPage = () => {
    const [contactNo, setContactNo] = useState("");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState<string | null>(null);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError("");

        if (!/^\d{10}$/.test(contactNo)) {
            setError("Please enter a valid 10-digit registered mobile number.");
            return;
        }

        setSubmitting(true);
        try {
            const res = await fetch(API_ENDPOINTS.DELETE_ACCOUNT_PUBLIC, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ contact_no: contactNo, reason }),
            });
            const data = await res.json();

            if (data.status === "success") {
                setSuccess(data.masked_contact);
            } else {
                setError(data.message || "Something went wrong. Please try again.");
            }
        } catch (err) {
            setError("Network error. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main className="ffp-page">
            {/* ── Header band ── */}
            <header className="ffs-hero">
                <div className="ffh-container ffs-hero-inner">
                    <div>
                        <nav className="ffs-crumbs" aria-label="Breadcrumb">
                            <Link to="/">Home</Link>
                            <span aria-hidden="true">/</span>
                            <span className="is-current">Delete account</span>
                        </nav>
                        <h1 className="ffs-hero-title">Delete your <em>account</em></h1>
                    </div>
                    <p className="ffs-hero-sub">Fashion Frame — Account &amp; data deletion request</p>
                </div>
            </header>

            <div className="ffh-container">
                {success ? (
                    <div className="ffp-done">
                        <span className="ffp-done-icon" aria-hidden="true">
                            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17l-5-5" />
                            </svg>
                        </span>
                        <h2 className="ffp-done-title">Deletion request <em>received</em></h2>
                        <p>
                            The account associated with mobile number <strong>{success}</strong> has
                            been deactivated and scheduled for deletion.
                        </p>
                        <p>
                            If you change your mind or believe this was a mistake, please contact our
                            support team within 7 days at{" "}
                            <a href={`mailto:${env.supportEmail}`}>{env.supportEmail}</a>.
                        </p>
                        <Link to="/" className="ffh-btn">Back to home</Link>
                    </div>
                ) : (
                    <div className="ffp-delete">
                        {/* What happens */}
                        <aside className="ffp-panel ffp-panel--soft">
                            <p className="ffh-eyebrow ffh-eyebrow--light ffh-eyebrow--left">Before you go</p>
                            <h2 className="ffp-panel-title">What happens when you <em>delete</em></h2>

                            <ol className="ffp-steps">
                                <li>
                                    <b>1</b>
                                    <span>Your profile, saved addresses and login access are deactivated immediately.</span>
                                </li>
                                <li>
                                    <b>2</b>
                                    <span>Order history and invoices are retained for a limited period, as required for accounting, tax and legal compliance.</span>
                                </li>
                                <li>
                                    <b>3</b>
                                    <span>Personal data (name, contact number, email, saved addresses) is permanently purged from active systems within 30 days.</span>
                                </li>
                                <li>
                                    <b>4</b>
                                    <span>This action cannot be undone after the 7-day grace period.</span>
                                </li>
                            </ol>
                        </aside>

                        {/* Form */}
                        <section className="ffp-form-card" aria-labelledby="ffp-del-title">
                            <h2 id="ffp-del-title" className="ffp-form-title">Request <em>deletion</em></h2>
                            <p className="ffp-form-sub">Enter the mobile number registered with your account.</p>

                            {error && (
                                <div className="ffp-notice ffp-notice--danger" role="alert">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.2" />
                                        <path d="M8 5v3.5M8 11v.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                    </svg>
                                    <span>{error}</span>
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="ffp-field">
                                    <label htmlFor="contact_no">Registered mobile number</label>
                                    <input
                                        type="tel"
                                        id="contact_no"
                                        inputMode="numeric"
                                        maxLength={10}
                                        placeholder="10-digit mobile number"
                                        value={contactNo}
                                        onChange={(e) => setContactNo(e.target.value.replace(/\D/g, ""))}
                                        required
                                    />
                                </div>

                                <div className="ffp-field">
                                    <label htmlFor="reason">Reason for leaving (optional)</label>
                                    <textarea
                                        id="reason"
                                        rows={4}
                                        placeholder="Help us improve — why are you deleting your account?"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                    />
                                </div>

                                <button type="submit" className="ffh-btn ffp-submit ffp-btn--danger" disabled={submitting}>
                                    {submitting ? "Submitting…" : "Request account deletion"}
                                </button>
                            </form>
                        </section>
                    </div>
                )}
            </div>
        </main>
    );
};

export default DeleteAccountPage;